"use client";

import { useState } from "react";
import { Activity, Gauge, Rocket } from "lucide-react";
export default function Process() {
  const [hoveredIndex, setHoveredIndex] = useState(0);
  const phases = [{
    eyebrow: "STAGE 01 / DISCOVERY",
    titleStart: "Understanding the process",
    italic: "",
    titleEnd: "",
    body: "We begin by analyzing your specific needs and strategic objectives. Our technical team designs a personalized roadmap to ensure every step is aligned with your long-term growth vision.",
    label: "Discovery",
    tPlus: "DAY 01",
    alt: "0.0",
    vel: "0.0"
  }, {
    eyebrow: "STAGE 02 / WORKFLOW REVIEW",
    titleStart: "Mapping manual steps",
    italic: "",
    titleEnd: "",
    body: "We develop the project architecture, defining key milestones and necessary resources. We establish work protocols to ensure efficient and transparent execution from the very beginning.",
    label: "Workflow Review",
    tPlus: "DAY 05",
    alt: "12.5",
    vel: "1.2"
  }, {
    eyebrow: "STAGE 03 / SOLUTION BUILD",
    titleStart: "Building the automation",
    italic: "",
    titleEnd: "",
    body: "Our technical team begins implementation, transforming concepts into tangible solutions. We maintain constant communication to adjust details and ensure maximum quality in every delivery.",
    label: "Solution Build",
    tPlus: "DAY 15",
    alt: "45.0",
    vel: "4.5"
  }, {
    eyebrow: "STAGE 04 / OPTIMISATION",
    titleStart: "Testing and refining",
    italic: "",
    titleEnd: "",
    body: "We refine every component of the project based on rigorous testing. We eliminate friction and maximize performance so the final result exceeds industry standards.",
    label: "Optimisation",
    tPlus: "DAY 25",
    alt: "88.2",
    vel: "6.8"
  }, {
    eyebrow: "STAGE 05 / IMPLEMENTATION",
    titleStart: "Putting it into use",
    italic: "",
    titleEnd: "",
    body: "We launch your solution with total security. We monitor the deployment in real-time to guarantee a smooth transition and an immediate impact on your daily operations.",
    label: "Implementation",
    tPlus: "DAY 30",
    alt: "100.0",
    vel: "8.2"
  }, {
    eyebrow: "STAGE 06 / SUPPORT",
    titleStart: "Supporting future improvements",
    italic: "",
    titleEnd: "",
    body: "We don't stop at delivery. We offer preventive maintenance and constant technical support to ensure your investment remains competitive and efficient over time.",
    label: "Support",
    tPlus: "DAY 30+",
    alt: "100.0",
    vel: "0.0"
  }];
  const activeIndex = hoveredIndex;
  const activePhase = phases[activeIndex];
  const waypoints = phases.map((_, i) => {
    const t = i / (phases.length - 1);
    return {
      x: 10 + t * 80,
      y: 88 - t * 76
    };
  });
  const lineFillT = activeIndex / (phases.length - 1);
  return <section data-section-id="10308" id="how-we-work" className="text-foreground relative overflow-hidden dark bg-accent">
      <div className="relative h-auto min-h-[800px] w-full lg:h-[90vh]">
        <div className="relative mx-auto flex h-full max-w-[1440px] flex-col px-5 pt-20 pb-24 md:px-6 md:pt-24 lg:grid lg:grid-cols-12 lg:gap-6 lg:px-8 lg:pt-28 lg:pb-28">
          {}
          <div className="relative hidden lg:col-span-8 lg:block">
            <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 h-full w-full">
              <line x1={waypoints[0].x} y1={waypoints[0].y} x2={waypoints[waypoints.length - 1].x} y2={waypoints[waypoints.length - 1].y} stroke="white" strokeWidth="0.18" vectorEffect="non-scaling-stroke" style={{
              strokeWidth: "3px",
              opacity: 0.3
            } as React.CSSProperties} />
              <line x1={waypoints[0].x} y1={waypoints[0].y} x2={waypoints[0].x + (waypoints[waypoints.length - 1].x - waypoints[0].x) * lineFillT} y2={waypoints[0].y + (waypoints[waypoints.length - 1].y - waypoints[0].y) * lineFillT} className="transition-all duration-500 ease-in-out" stroke="var(--primary)" vectorEffect="non-scaling-stroke" style={{
              strokeWidth: "3px"
            } as React.CSSProperties} />
            </svg>

            <div className="absolute inset-0 bg-accent">
              {waypoints.map((w, i) => {
              const isActive = i === activeIndex;
              const isCompleted = i < activeIndex;
              const size = isActive ? 20 : 14;
              return <div data-index={i} key={i} className="absolute cursor-pointer group" style={{
                left: `${w.x}%`,
                top: `${w.y}%`,
                transform: "translate(-50%, -50%)"
              }} onMouseEnter={() => setHoveredIndex(i)}>
                    <div className="flex items-center gap-4">
                      <div className="transition-all duration-300 ease-out rounded-lg" style={{
                    width: `${size}px`,
                    height: `${size}px`,
                    background: isActive ? "var(--primary)" : isCompleted ? "var(--foreground)" : "transparent",
                    border: isCompleted ? "2px solid var(--foreground)" : isActive ? "2px solid var(--primary)" : "2px solid var(--muted-foreground)",
                    boxShadow: isActive ? "0 0 25px 6px hsla(199, 89%, 48%, 0.4)" : "none"
                  }} />
                      <div className="whitespace-nowrap font-default text-sm font-medium transition-all duration-300" style={{
                    color: isActive ? "var(--foreground)" : isCompleted ? "var(--foreground)" : "var(--muted-foreground)",
                    transform: isActive ? "translateX(4px)" : "none"
                  }}>
                        {phases[i].label}
                      </div>
                    </div>
                  </div>;
            })}
            </div>
          </div>

          {}
          <div className="hidden lg:col-span-4 lg:flex lg:flex-col lg:justify-center">
            <div className="font-mono uppercase" style={{
            fontSize: "11px",
            letterSpacing: "0.18em",
            color: "var(--muted-foreground)"
          }}>
              OUR APPROACH &middot; 6 STAGES
            </div>
            <div className="mt-6 min-h-[280px]">
              <PhaseText phase={activePhase} key={activeIndex} />
            </div>

            <div className="mt-10 flex items-center gap-3">
              <div className="font-mono" style={{
              fontSize: "11px",
              letterSpacing: "0.18em",
              color: "var(--muted-foreground)"
            }}>
                {String(activeIndex + 1).padStart(2, "0")} /{" "}
                {String(phases.length).padStart(2, "0")}
              </div>
              <div className="h-px flex-1 bg-border" />
            </div>
          </div>

          {}
          <div className="flex h-full flex-col lg:hidden">
            <div className="font-mono uppercase" style={{
            fontSize: "11px",
            letterSpacing: "0.18em",
            color: "var(--muted-foreground)"
          }}>
              OUR APPROACH &middot; 6 STAGES
            </div>

            <div className="mt-8 flex-1">
              <PhaseText phase={activePhase} key={`m-${activeIndex}`} />
            </div>

            <div className="relative mt-12 mb-8 h-16">
              <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2">
                <div className="relative h-[2px] w-full bg-muted-foreground/30">
                  <div className="absolute left-0 top-0 h-[2px] transition-[width] duration-500 ease-out" style={{
                  width: `${lineFillT * 100}%`,
                  background: "var(--primary)"
                }} />
                </div>
                <div className="relative mt-0">
                  {phases.map((p, i) => {
                  const t = i / (phases.length - 1);
                  const isActive = i === activeIndex;
                  const isCompleted = i < activeIndex;
                  return <div data-index={i} key={i} className="absolute cursor-pointer" style={{
                    left: `${t * 100}%`,
                    top: "-5px",
                    transform: "translate(-50%, -50%)"
                  }} onClick={() => setHoveredIndex(i)}>
                        <div className="transition-all duration-300 ease-out rounded-lg" style={{
                      width: isActive ? "18px" : "12px",
                      height: isActive ? "18px" : "12px",
                      background: isActive ? "var(--primary)" : isCompleted ? "var(--foreground)" : "transparent",
                      border: isCompleted ? "2px solid var(--foreground)" : isActive ? "2px solid var(--primary)" : "2px solid var(--muted-foreground)",
                      boxShadow: isActive ? "0 0 20px 4px hsla(199, 89%, 48%, 0.4)" : "none"
                    }} />
                        {isActive && <div className="absolute left-1/2 mt-4 -translate-x-1/2 whitespace-nowrap font-default text-xs font-medium" style={{
                      color: "var(--foreground)"
                    }}>
                            {p.label}
                          </div>}
                      </div>;
                })}
                </div>
              </div>
            </div>
          </div>
        </div>

        {}
        <div className="absolute bottom-0 left-0 right-0 h-16 border-t backdrop-blur-sm bg-accent" style={{
        borderColor: "var(--border)"
      }}>
          <div className="absolute left-0 right-0 top-0 h-px bg-border">
            <div className="h-px transition-[width] duration-500 ease-out" style={{
            width: `${lineFillT * 100}%`,
            background: "var(--primary)"
          }} />
          </div>

          <div className="mx-auto flex h-full max-w-[1440px] items-center justify-between px-5 md:px-6 lg:px-8">
            <div className="flex items-center gap-2">
              <span className="inline-block h-1.5 w-1.5 rounded-lg" style={{
              background: "var(--primary)",
              animation: "apogee-pulse 1.6s ease-in-out infinite"
            }} />
              <span className="font-mono uppercase text-primary bg-accent" style={{
              fontSize: "11px",
              letterSpacing: "0.18em",
              color: "var(--foreground)"
            }}>STAGE 03 /BUILD ACTIVE / FLOW READY / VALIDATION IN PROGRESS</span>
            </div>

            <div className="flex items-center gap-6 sm:gap-10">
              <TelemetryItem icon={<Rocket className="lucide lucide-rocket h-3.5 w-3.5 text-foreground" strokeWidth={1.25} />} label="TIME" value={activePhase.tPlus} />
              <TelemetryItem icon={<Gauge className="h-3.5 w-3.5" strokeWidth={1.25} />} label="PROGRESS" value={activePhase.alt} />
              <TelemetryItem icon={<Activity className="h-3.5 w-3.5" strokeWidth={1.25} />} label="EFFICIENCY" value={activePhase.vel} />
            </div>
          </div>
        </div>

        <style>{`
          @keyframes apogee-pulse {
            0%, 100% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.3); opacity: 0.7; }
          }
          @keyframes apogee-fadeup {
            from { opacity: 0; transform: translateY(12px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </div>
    </section>;
}
function PhaseText({
  phase
}: {
  phase: {
    eyebrow: string;
    titleStart: string;
    italic: string;
    titleEnd: string;
    body: string;
  };
}) {
  return <div style={{
    animation: "apogee-fadeup 320ms cubic-bezier(0.2,0.8,0.2,1) both"
  }}>
      <div className="font-mono uppercase" style={{
      fontSize: "11px",
      letterSpacing: "0.18em",
      color: "#FFFFFF"
    }}>
        {phase.eyebrow}
      </div>
      <h3 className="mt-4 font-default" style={{
      fontSize: "clamp(1.75rem, 3vw, 2.25rem)",
      fontWeight: 600,
      letterSpacing: "-0.03em",
      lineHeight: 1.05,
      color: "var(--foreground)"
    }}>
        {phase.titleStart}
        {phase.italic && <em className="font-default italic" style={{
        color: "var(--primary)",
        fontWeight: 600
      }}>
            {phase.italic}
          </em>}
        {phase.titleEnd}
      </h3>
      <p className="mt-5 font-default" style={{
      fontSize: "1rem",
      lineHeight: 1.6,
      color: "var(--muted-foreground)",
      maxWidth: "38ch"
    }}>
        {phase.body}
      </p>
    </div>;
}
function TelemetryItem({
  icon,
  label,
  value
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return <div className="flex items-center gap-2 bg-accent">
      <span style={{
      color: "var(--muted-foreground)"
    }}>{icon}</span>
      <div className="flex items-baseline gap-2">
        <span className="font-mono uppercase hidden sm:inline" style={{
        fontSize: "10px",
        letterSpacing: "0.18em",
        color: "var(--muted-foreground)"
      }}>
          {label}
        </span>
        
      </div>
    </div>;
}